import { usePage, useRemember } from "@inertiajs/inertia-react";
import { useState } from "react";
import { chartColors, DashboardLineChart } from "@/Utilities/V2Graphs";
import { Subheader } from "@/Components";
import Placeholder from "../Placeholder";
import { useLoading } from "@/Contexts/LoadingContext";
import { convertToHumanReadable } from "@/Utilities/Utilities";
import { IconListUl, IconLineGraph } from "@/Icons";

const EmissionGraphPerTeams = ({ entries, labels }) => {
  const { isLoaded } = useLoading();

  const teams = usePage().props?.teams?.data || [];

  const DISPLAY = {
    CHART: "chart",
    LIST: "list",
  };

  const [displayMode, setDisplayMode] = useRemember(
    DISPLAY.CHART,
    "Chart/Teams/DisplayMode",
  );
  const [teamFilter, setTeamFilter] = useState(teams.map((team) => team.id));

  // Create dataSets for the chart
  const dataSetsPerTeam =
    Object.keys(entries.teams)
      .map((teamId, i) => {
        const teamEntries = entries.teams[teamId];

        if (!teamFilter.length || !teamFilter.includes(Number(teamId))) {
          return;
        }

        return {
          label: teamEntries.label,
          data: labels.map((label) => {
            return teamEntries.data[label.key]?.total || 0;
          }),
          barPercentage: 0.5,
          backgroundColor: chartColors[i],
        };
      })
      .filter((dataSet) => dataSet) || [];

  return (
    <div className="bg-gray-light px-8 pb-8 pt-4">
      <Subheader>EMISSIONS PER TEAM</Subheader>

      <div
        className={`w-full ${
          displayMode === DISPLAY.CHART ? "block" : "hidden"
        }`}
      >
        {isLoaded ? (
          <DashboardLineChart
            dataSets={dataSetsPerTeam}
            labels={labels.map((l) => l.name)}
          />
        ) : (
          <Placeholder width="100%" height="350px" />
        )}
      </div>

      <div
        className={`w-full ${displayMode === DISPLAY.LIST ? "flex" : "hidden"}`}
      >
        <div className="flex w-1/4 flex-col gap-1">
          <p className="mb-4 flex shrink-0 flex-col justify-end font-bold">
            <span>&nbsp;</span>
            <span>Team</span>
          </p>
          {dataSetsPerTeam.map((dataSet, i) => {
            return (
              <div key={i} className="flex justify-between gap-4">
                <p>{dataSet.label}</p>
              </div>
            );
          }) || []}
        </div>
        <div className="w-4/5">
          <div className="gap- flex flex-col overflow-scroll pb-8">
            <div className="mb-4 flex gap-4">
              {labels.map((label, i) => {
                return (
                  <p
                    key={i}
                    className="flex w-[100px] shrink-0 flex-col justify-end font-bold"
                  >
                    {
                      // Separate year and month into its own spans
                      label.name.split(" ").map((text, j) => {
                        return <span key={j}>{text}</span>;
                      })
                    }
                  </p>
                );
              })}
            </div>
            {dataSetsPerTeam.map((dataSet, i) => {
              return (
                <div className="flex gap-4" key={i}>
                  {dataSet.data.map((value, j) => {
                    return (
                      <p key={j} className="w-[100px] shrink-0">
                        {convertToHumanReadable(value)}
                      </p>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-4 flex w-full gap-4">
        <section className="flex w-full flex-wrap gap-x-8 gap-y-4 md:w-2/3">
          {Object.keys(entries.teams).map((team_id, i) => {
            const team = teams.find((t) => t.id === Number(team_id));

            return (
              <div
                key={i}
                className="flex cursor-pointer items-center gap-2"
                onClick={() => {
                  if (teamFilter.includes(team.id)) {
                    setTeamFilter(teamFilter.filter((s) => s !== team.id));
                  } else {
                    setTeamFilter([...teamFilter, team.id]);
                  }
                }}
              >
                <span
                  className={`h-3 w-3 rounded-[2px] border-[1px] border-white outline outline-1 ${
                    teamFilter.includes(team.id) ? "" : "bg-white"
                  }`}
                  style={{
                    backgroundColor: teamFilter.includes(team.id)
                      ? chartColors[i]
                      : "",
                    outlineColor: chartColors[i],
                  }}
                ></span>
                <span>{team.name}</span>
              </div>
            );
          })}
        </section>
        <section className="flex w-full items-end justify-end gap-2 md:w-1/3">
          <button
            className={`shadow-small p-2 ${
              displayMode === DISPLAY.CHART
                ? "bg-primary text-white"
                : "bg-white"
            }`}
            onClick={() => setDisplayMode(DISPLAY.CHART)}
          >
            <IconLineGraph size={24} />
          </button>
          <button
            className={`shadow-small p-2 ${
              displayMode === DISPLAY.LIST
                ? "bg-primary text-white"
                : "bg-white"
            }`}
            onClick={() => setDisplayMode(DISPLAY.LIST)}
          >
            <IconListUl size={24} />
          </button>
        </section>
      </div>
    </div>
  );
};

export default EmissionGraphPerTeams;
